import React, { useMemo, useState, useEffect, useRef } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { getPendingPayments } from "../../apis/pendingPayments";
import { format } from "date-fns";
import { log } from "console";

type BillingType = "opd" | "ipd" | "lab" | "pharmacy";

type PendingPayment = {
  id: string; // unique per row (type_code + reference) to avoid duplicate keys
  patientName: string;
  typeLabel: string;
  typeCode: "opd" | "ipd" | "lab_order" | "pharmacy";
  reference: string;
  amount: number;
  date: string | null;
};

type Summary = {
  totalPending: number;
  opdPending: number;
  ipdPending: number;
  labPending: number;
  pharmacyPending: number;
};

const BillingList: React.FC = () => {
  const [activeType, setActiveType] = useState<BillingType | "all">("all");
  const [searchText, setSearchText] = useState("");
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [summary, setSummary] = useState<Summary>({
    totalPending: 0,
    opdPending: 0,
    ipdPending: 0,
    labPending: 0,
    pharmacyPending: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const totals = useMemo(() => {
    return {
      all: summary.totalPending,
      opd: summary.opdPending,
      ipd: summary.ipdPending,
      lab: summary.labPending,
      pharmacy: summary.pharmacyPending,
    };
  }, [summary]);

  const typeLabels: Record<BillingType | "all", string> = {
    all: "All",
    opd: "OPD Visits",
    ipd: "IPD Visits",
    lab: "Lab Orders",
    pharmacy: "Pharmacy",
  };

  const mapTypeToApi = (type: BillingType) => {
    if (type === "lab") return "lab_order";
    return type;
  };

  const formatType = (typeCode: PendingPayment["typeCode"]) => {
    switch (typeCode) {
      case "opd":
        return "OPD Visit";
      case "ipd":
        return "IPD Visit";
      case "lab_order":
        return "Lab Order";
      case "pharmacy":
        return "Pharmacy";
      default:
        return typeCode;
    }
  };

  const filteredPayments = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return pendingPayments;
    return pendingPayments.filter(
      (payment) =>
        payment.patientName.toLowerCase().includes(normalizedSearch) ||
        payment.reference.toLowerCase().includes(normalizedSearch)
    );
  }, [searchText, pendingPayments]);

  useEffect(() => {
    const params: any = {};
    if (activeType !== "all") {
      params.type = mapTypeToApi(activeType);
    }
    if (searchText.trim()) {
      params.search = searchText.trim();
    }

    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    setPendingPayments([]);

    getPendingPayments(params)
      .then((res: any) => {
        if (currentRequestId !== requestIdRef.current) return;
        const data = res?.data ?? res;
        const items = data?.data ?? [];
        const mappedItems: PendingPayment[] = items.map((item: any) => {
          const typeCode = item.typeCode ?? item.type_code ?? "opd";
          const reference = item.referenceNumber ?? item.reference_number ?? "-";
          return {
            id: item.sourceId ?? item.source_id ?? `${typeCode}-${reference}`,
            patientName: item.patientName ?? item.patient_name ?? "",
            typeLabel: item.type ?? "",
            typeCode,
            reference,
            amount: Number(item.amount ?? 0),
            date: item.dueDate ?? item.due_date ?? item.date ?? null,
          };
        });
        console.log("Mapped pending payments:", mappedItems);
        setPendingPayments(mappedItems);
        setSummary({
          totalPending: Number(
            data?.summary?.totalPending ?? data?.summary?.total_pending ?? 0
          ),
          opdPending: Number(
            data?.summary?.opdPendingAmount ??
              data?.summary?.opd_pending_amount ??
              0
          ),
          ipdPending: Number(
            data?.summary?.ipdPendingAmount ??
              data?.summary?.ipd_pending_amount ??
              0
          ),
          labPending: Number(
            data?.summary?.labPendingAmount ??
              data?.summary?.lab_pending_amount ??
              0
          ),
          pharmacyPending: Number(
            data?.summary?.pharmacyPendingAmount ??
              data?.summary?.pharmacy_pending_amount ??
              0
          ),
        });
      })
      .catch((error) => {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("Failed to load pending payments", error);
      })
      .finally(() => {
        if (currentRequestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  }, [activeType, searchText]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Billing
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Pending payments across OPD, IPD, Lab, and Pharmacy.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 mt-6 sm:grid-cols-2 lg:grid-cols-5">
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">Total Pending</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">₹{totals.all}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">OPD Pending</p>
          <p className="mt-2 text-2xl font-bold text-blue-600">₹{totals.opd}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">IPD Pending</p>
          <p className="mt-2 text-2xl font-bold text-indigo-600">₹{totals.ipd}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">Lab Pending</p>
          <p className="mt-2 text-2xl font-bold text-amber-600">₹{totals.lab}</p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm font-medium text-gray-600">Pharmacy Pending</p>
          <p className="mt-2 text-2xl font-bold text-emerald-600">
            ₹{totals.pharmacy}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {(["all", "opd", "ipd", "lab", "pharmacy"] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setActiveType(type)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${activeType === type
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              {typeLabels[type]}
            </button>
          ))}
        </div>
        <div className="relative w-full max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search by patient or reference"
            className="py-2 pr-3 pl-10 w-full text-sm rounded-md border border-gray-300 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      <div className="flow-root mt-6">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Patient
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Reference
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Due Date
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayments.length > 0 ? (
                    filteredPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
                          {payment.patientName}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {payment.typeLabel || formatType(payment.typeCode)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {payment.reference}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {payment.date ? format(new Date(payment.date), "dd MMM yyyy") : "-"}
                        </td>
                        <td className="px-3 py-4 text-sm font-semibold text-gray-900">
                          ₹{payment.amount}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <button
                            type="button"
                            className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
                          >
                            Collect Payment
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-10 text-sm text-center text-gray-500"
                      >
                        {isLoading ? "Loading pending payments..." : "No pending payments found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingList;
